import { useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Generic inquiry modal used by Service cards and Product Book-Demo CTAs.
 * type: 'service' | 'product'
 * itemKey: service.key or product.slug
 * itemLabel: human readable name
 */
export default function InquiryModal({ open, onClose, type, itemKey, itemLabel, accent = "#FF6B1A" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", budget: "", timeline: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        inquiry_type: type,
        item_key: itemKey,
        item_label: itemLabel,
      });
      setDone(true);
      toast.success("Inquiry sent — we'll be in touch within 24 hrs.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    // reset for next open
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", email: "", phone: "", company: "", message: "", budget: "", timeline: "" });
    }, 300);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="inquiry-modal" onClick={close}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="relative w-full max-w-xl my-8 rounded-3xl glass overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-32 -right-32 w-[260px] h-[260px] rounded-full blur-3xl pointer-events-none opacity-40" style={{ background: accent }} />
        <button onClick={close} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 z-10" data-testid="inquiry-modal-close" aria-label="Close">
          <X size={18} />
        </button>

        {done ? (
          <div className="relative p-10 text-center">
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5"
              style={{ background: `${accent}33`, border: `1px solid ${accent}66` }}
            >
              <Check style={{ color: accent }} size={28} />
            </motion.div>
            <h3 className="text-2xl font-bold">Thanks, {form.name?.split(" ")[0] || "there"}!</h3>
            <p className="text-zinc-400 mt-2">Your inquiry for <span className="text-white font-medium">{itemLabel}</span> has been received. We'll reach out within 24 hours.</p>
            <button onClick={close} className="mt-6 px-6 py-2.5 rounded-full font-semibold text-black" style={{ background: `linear-gradient(135deg, ${accent}, #FFAA33)` }}>
              Close
            </button>
          </div>
        ) : (
          <div className="relative p-7 sm:p-8">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} style={{ color: accent }} />
              <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: accent }}>
                {type === "product" ? "Book a demo" : "Get a quote"}
              </span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold leading-tight">{itemLabel}</h3>
            <p className="text-sm text-zinc-400 mt-1">Tell us a bit about your project — we'll get back within 24 hours.</p>

            <form onSubmit={onSubmit} className="mt-6 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full name" name="name" value={form.name} onChange={onChange} required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} required />
                <Field label="Company (optional)" name="company" value={form.company} onChange={onChange} />
                <Field label="Budget (optional)" name="budget" value={form.budget} onChange={onChange} placeholder="e.g. ₹2-5 lakh" />
                <Field label="Timeline (optional)" name="timeline" value={form.timeline} onChange={onChange} placeholder="e.g. 2 months" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400">Project details</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  placeholder={type === "product" ? "What features matter most? Any specific use-case?" : "What are you trying to build?"}
                  className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[var(--accent,#FF6B1A)]"
                  style={{ "--accent": accent }}
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit-btn"
                className="w-full py-3.5 rounded-full text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: `linear-gradient(135deg, ${accent}, #FFAA33, #3B82F6)` }}
              >
                {submitting ? "Sending..." : <>Submit Inquiry <Send size={14} /></>}
              </motion.button>
              <p className="text-[11px] text-zinc-500 text-center pt-1">We respect your privacy. No spam, ever.</p>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-400">{label}</label>
      <input
        {...props}
        className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A] transition-colors"
      />
    </div>
  );
}
